import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import config, { CHAIN } from "@/config/config";
import { ChainType } from "@/constants/enum/enum";

export interface TokenPricesState {
  nativePrice: number;
  loadingNativePrice: boolean;
  mafiaPrice: number;
  loadingMafiaPrice: boolean;
}

const initialState: TokenPricesState = {
  nativePrice: 0,
  loadingNativePrice: false,
  mafiaPrice: 0,
  loadingMafiaPrice: false,
};

interface DexScreenerPair {
  baseToken?: { address?: string };
  quoteToken?: { address?: string };
  priceNative?: string;
  priceUsd?: string;
  liquidity?: { usd?: number };
}

export const getTokenPriceUsd = (
  pair: DexScreenerPair,
  tokenAddress: string
) => {
  const target = tokenAddress.toLowerCase();
  const priceUsd = Number(pair.priceUsd);

  if (!Number.isFinite(priceUsd) || priceUsd <= 0) return 0;
  if (pair.baseToken?.address?.toLowerCase() === target) return priceUsd;

  if (pair.quoteToken?.address?.toLowerCase() === target) {
    const basePriceInQuote = Number(pair.priceNative);
    return Number.isFinite(basePriceInQuote) && basePriceInQuote > 0
      ? priceUsd / basePriceInQuote
      : 0;
  }

  return 0;
};

export const fetchNativePrice = createAsyncThunk(
  "tokenPrices/fetchNativePrice",
  async () => {
    const response = await axios.get(config.nativePriceUrl);
    return CHAIN === ChainType.PLS
      ? response.data.pulsechain.usd
      : response.data.binancecoin.usd;
  }
);

export const fetchMafiaPrice = createAsyncThunk(
  "tokenPrices/fetchMafiaPrice",
  async () => {
    try {
      const response = await axios.get(
        `${config.mafiaPriceUrl}${config.pairAddress}`
      );
      const configuredPair = response.data.pair ?? response.data.pairs?.[0];
      const configuredPrice = configuredPair
        ? getTokenPriceUsd(configuredPair, config.mafiaAddress)
        : 0;

      if (configuredPrice > 0) return configuredPrice;
    } catch {
      // The configured pair may have been removed; discover current pairs below.
    }

    const chainId = CHAIN === ChainType.PLS ? "pulsechain" : "bsc";
    const response = await axios.get(
      `https://api.dexscreener.com/token-pairs/v1/${chainId}/${config.mafiaAddress}`
    );
    const pairs: DexScreenerPair[] = Array.isArray(response.data)
      ? response.data
      : [];
    const activePair = pairs
      .map((pair) => ({
        liquidity: Number(pair.liquidity?.usd) || 0,
        price: getTokenPriceUsd(pair, config.mafiaAddress),
      }))
      .filter(({ price }) => price > 0)
      .sort((a, b) => b.liquidity - a.liquidity)[0];

    if (!activePair) throw new Error("Unable to determine the MAFIA price");
    return activePair.price;
  }
);

const tokenPricesSlice = createSlice({
  name: "tokenPrices",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchNativePrice.pending, (state) => {
      state.loadingNativePrice = true;
    });
    builder.addCase(fetchNativePrice.fulfilled, (state, { payload }) => {
      state.loadingNativePrice = false;
      state.nativePrice = payload;
    });
    builder.addCase(fetchNativePrice.rejected, (state) => {
      state.loadingNativePrice = false;
    });
    builder.addCase(fetchMafiaPrice.pending, (state) => {
      state.loadingMafiaPrice = true;
    });
    builder.addCase(fetchMafiaPrice.fulfilled, (state, { payload }) => {
      state.loadingMafiaPrice = false;
      state.mafiaPrice = payload;
    });
    builder.addCase(fetchMafiaPrice.rejected, (state) => {
      state.loadingMafiaPrice = false;
    });
  },
});

export default tokenPricesSlice.reducer;
